import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { CheckCircle, Mail, Calendar } from 'lucide-react';

const ConfirmationPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { bookingReference, email } = location.state || {};

    useEffect(() => {
        if (!bookingReference) {
            navigate('/');
        }
    }, [bookingReference, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#fcf6e3' }}>
            <motion.div 
                className="card p-8 sm:p-12 max-w-2xl mx-6 text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
            >
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="flex justify-center mb-6"
                >
                    <CheckCircle className="w-20 h-20" style={{ color: '#d9a520' }} data-testid="success-icon" />
                </motion.div>

                <h1 
                    className="text-3xl sm:text-4xl font-bold mb-4" 
                    style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                    data-testid="confirmation-title"
                >
                    Booking Confirmed!
                </h1>

                <p className="text-lg mb-6" style={{ color: '#2d2a2a' }}>
                    Thank you for booking with Dr. Kathrine Monato.
                </p>

                <div className="bg-secondary/10 p-6 rounded-lg mb-8">
                    <p className="text-sm text-muted-foreground mb-2">Your Booking Reference</p>
                    <p 
                        className="text-2xl font-bold" 
                        style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                        data-testid="booking-reference"
                    >
                        {bookingReference}
                    </p>
                </div>

                <div className="space-y-4 mb-8 text-left">
                    <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 mt-1" style={{ color: '#d9a520' }} />
                        <div>
                            <p className="font-semibold" style={{ color: '#701a25' }}>Confirmation Email Sent</p>
                            <p className="text-sm text-muted-foreground">
                                We've sent a confirmation email to <strong>{email}</strong> with all the appointment details.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 mt-1" style={{ color: '#d9a520' }} />
                        <div>
                            <p className="font-semibold" style={{ color: '#701a25' }}>What's Next?</p>
                            <p className="text-sm text-muted-foreground">
                                Please arrive 10 minutes before your scheduled appointment time. Bring your booking reference for check-in.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                        onClick={() => navigate('/')}
                        className="btn-primary"
                        data-testid="back-to-home-btn"
                    >
                        Back to Home
                    </Button>
                    <Button 
                        onClick={() => navigate('/booking')}
                        className="btn-secondary"
                        data-testid="book-another-btn"
                    >
                        Book Another Appointment
                    </Button>
                </div>

                <p className="text-xs text-muted-foreground mt-8">
                    For any questions or changes to your appointment, please reply to the confirmation email.
                </p>
            </motion.div>
        </div>
    );
};

export default ConfirmationPage;